import { RcFile } from 'antd/lib/upload';
import { EMeasureUnit } from '../../store/commomTypes';
import {
  ILastOrder,
  ILastOrderItem
} from '../../store/ducks/long-steel-order-item-search/types';
import {
  ILongSteelOrderItem,
  IItemSaleInfo
} from '../../store/ducks/long-steel-shopping-cart/types';
import {
  ECodeType,
  ESpreadsheetField,
  IOrderEntryPreference
} from '../../store/ducks/user/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { EGerdauService } from '../EGerdauService';
import { sanitizeFileName } from '../../utils/sanitizeUtils';

export class OrderItemSearchService extends EGerdauService {
  async searchItems(
    customerId: string,
    searchTerm: string,
    codeType: ECodeType,
    customerState: string
  ): Promise<ILongSteelOrderItem[]> {
    const formData = new FormData();

    formData.append('content', searchTerm);
    formData.append(
      'json',
      JSON.stringify({
        startRow: 2,
        positions: [
          {
            field: codeType,
            position: 'A'
          }
        ],
        soldTo: customerId,
        shipTo: customerId
      })
    );

    const headers = this.getHeaders();

    const response = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.SEARCH_ITEM}?customer=${customerId}`,
      formData,
      { headers }
    );

    const items: ILongSteelOrderItem[] = response.data.items;

    this.setFoundItem(items);

    await this.loadPaymentTermsIfNeed(items, customerId, customerState);

    this.initSaleOrderInfo(items);
    this.createConsumptionDestinationToItems(items);

    return items;
  }

  private async loadPaymentTermsIfNeed(
    items: ILongSteelOrderItem[],
    customerId: string,
    customerState: string
  ) {
    if (
      items.some(
        (item) =>
          item.found &&
          (!item.destinations[0]?.paymentTerms ||
            item.destinations[0]?.paymentTerms?.length === 0)
      )
    ) {
      const bodyRequest = {
        customer: customerId,
        state: customerState,
        products: items
          .filter(
            (item) =>
              item.found &&
              item.destinations.some(
                (dest) => !dest.paymentTerms || dest.paymentTerms?.length === 0
              )
          )
          .map((item) => {
            const destination = item.destinations[0];
            return {
              sku: item.sku,
              division: destination.division,
              company: destination.company,
              salesOrg: destination.salesOrg,
              distributionChannel: destination.distributionChannel
            };
          })
      };

      const headers = super.getHeaders();
      const response = await customersAuthorizationApi.post(
        ECustomerAuthorizationEndpoints.PAYMENT_CONDITIONS,
        bodyRequest,
        { headers }
      );

      items
        .filter(
          (item) =>
            item.found &&
            item.destinations.some(
              (dest) => !dest.paymentTerms || dest.paymentTerms?.length === 0
            )
        )
        .forEach((item) => {
          item.destinations.forEach((dest) => {
            dest.paymentTerms = response.data.paytermCondition.map(
              (pc: {
                paymentTerms: string;
                paymentTermsDescription: string;
              }) => {
                return {
                  term: pc.paymentTerms,
                  description: pc.paymentTermsDescription
                };
              }
            );
          });
        });
    }
  }

  private initSaleOrderInfo(items: ILongSteelOrderItem[]) {
    items.forEach((item) => {
      if (item.found) {
        item.itemSaleInfo = {
          material: item.sku
        };
      }
    });
  }

  async searchItemsBySpreadsheet(
    customerId: string,
    orderEntryPreference: IOrderEntryPreference,
    file: RcFile,
    customerState: string
  ): Promise<ILongSteelOrderItem[]> {
    const headers = super.getHeaders();

    const getFirstPosition = () => {
      if (orderEntryPreference.codeType === ECodeType.SKU) {
        return orderEntryPreference.spreadsheetColumns.find(
          (oep) => oep.field === ESpreadsheetField.SKU
        )?.position;
      } else if (orderEntryPreference.codeType === ECodeType.CLIENT_SKU) {
        return orderEntryPreference.spreadsheetColumns.find(
          (oep) => oep.field === ESpreadsheetField.CLIENT_SKU
        )?.position;
      } else {
        return '';
      }
    };

    const firstPosition = getFirstPosition();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestJSON: any = {
      startRow: orderEntryPreference.spreadsheetLine,
      positions: [
        [
          {
            field: orderEntryPreference.codeType,
            position: firstPosition
          }
        ]
      ],
      soldTo: customerId,
      shipTo: customerId
    };

    orderEntryPreference.spreadsheetColumns.forEach((column) => {
      if (
        column.field !== ESpreadsheetField.SKU &&
        column.field !== ESpreadsheetField.CLIENT_SKU &&
        column.position
      ) {
        requestJSON.positions.push({
          field: column.field,
          position: column.position
        });
      }
    });
    const sanitizedFileName = sanitizeFileName(file.name);
    const sanitizedFile = new File([file], sanitizedFileName, {
      type: file.type
    });

    const formData = new FormData();
    formData.append('json', JSON.stringify(requestJSON));
    formData.append('file', sanitizedFile);
    formData.append(
      'Content-Type',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );

    const response = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.SEARCH_ITEM_SHEET}?customer=${customerId}`,
      formData,
      { headers }
    );

    const items = response.data.items as ILongSteelOrderItem[];
    localStorage.setItem('itemsNotSent', response.data.itemsNotSent);
    this.setFoundItem(items);
    await this.loadPaymentTermsIfNeed(items, customerId, customerState);
    this.initItemsSaleInfoFromSpreadsheet(items);
    this.createConsumptionDestinationToItems(items);

    return items;
  }

  async searchItemsBySpreadsheetMvp(
    customerId: string,
    orderEntryPreference: IOrderEntryPreference,
    file: RcFile,
    customerState: string,
    setItemsNotSend: (arr: ILongSteelOrderItem[]) => void
  ): Promise<ILongSteelOrderItem[]> {
    const headers = super.getHeaders();

    const getFirstPosition = () => {
      if (orderEntryPreference.codeType === ECodeType.SKU) {
        return orderEntryPreference.spreadsheetColumns.find(
          (oep) => oep.field === ESpreadsheetField.SKU
        )?.position;
      } else if (orderEntryPreference.codeType === ECodeType.CLIENT_SKU) {
        return orderEntryPreference.spreadsheetColumns.find(
          (oep) => oep.field === ESpreadsheetField.CLIENT_SKU
        )?.position;
      } else {
        return '';
      }
    };

    const firstPosition = getFirstPosition();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestJSON: any = {
      startRow: orderEntryPreference.spreadsheetLine,
      positions: [
        [
          {
            field: orderEntryPreference.codeType,
            position: firstPosition
          }
        ]
      ],
      soldTo: customerId,
      shipTo: customerId
    };

    orderEntryPreference.spreadsheetColumns.forEach((column) => {
      if (
        column.field !== ESpreadsheetField.SKU &&
        column.field !== ESpreadsheetField.CLIENT_SKU &&
        column.position
      ) {
        requestJSON.positions.push({
          field: column.field,
          position: column.position
        });
      }
    });
    const sanitizedFileName = sanitizeFileName(file.name);
    const sanitizedFile = new File([file], sanitizedFileName, {
      type: file.type
    });

    const formData = new FormData();
    formData.append('json', JSON.stringify(requestJSON));
    formData.append('file', sanitizedFile);
    formData.append(
      'Content-Type',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );

    const response = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.SEARCH_ITEM_SHEET}?customer=${customerId}`,
      formData,
      { headers }
    );

    const items = response.data.items as ILongSteelOrderItem[];
    localStorage.setItem('itemsNotSent', response.data.itemsNotSent);
    this.setFoundItem(items);
    this.initItemsSaleInfoFromSpreadsheet(items);

    setItemsNotSend(response.data.itemsNotSent ?? []);

    return items;
  }

  private initItemsSaleInfoFromSpreadsheet(items: ILongSteelOrderItem[]) {
    items.forEach((item) => {
      if (item.found) {
        item.itemSaleInfo = {
          material: item.sku,
          // requiredDate: item.desiredDate && desiredDate.isSameOrAfter(minimunDate) ? item.desiredDate : '',
          requiredDate: item.desiredDate,
          quantity: item.quantity ? item.quantity.toFixed(3).toString() : '',
          unityMeasure: item.measureUnit,
          customerPurchaseItem: item.customerPurchaseItem || ''
        };
      }
    });
  }

  private setFoundItem(items: ILongSteelOrderItem[]) {
    items.forEach((item) => {
      const isExists = item.itemWeb !== undefined;
      item.found = isExists;
      item.addedToCart = isExists;
    });
  }

  private createConsumptionDestinationToItems(items: ILongSteelOrderItem[]) {
    const condGroup = ['CC', 'CI', 'DC', 'IC'];

    items.forEach((item) => {
      if (item.found) {
        item.destinations.forEach((element) => {
          if (condGroup.indexOf(element.condGroup) > -1) {
            item.destinations.push({
              ...item.destinations[0],
              description: 'Consumo',
              condGroup: 'CC'
            });
          }
        });
      }
    });
  }

  async searchLastOrders(customerId: string): Promise<ILastOrder[]> {
    const headers = this.getHeaders();
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.LAST_ORDERS}?customer=${customerId}`,
      { headers }
    );
    const lastOrders: ILastOrder[] = response.data.lastOrders;
    return lastOrders;
  }

  async searchItemsFromLastOrder(
    lastOrder: ILastOrder,
    customerState: string
  ): Promise<ILongSteelOrderItem[]> {
    const skus = lastOrder.items.map((item) => item.product).join('\n');

    const items = await this.searchItems(
      lastOrder.customerSoldTo,
      skus,
      ECodeType.SKU,
      customerState
    );

    items.forEach((item) => {
      const itemLastOrder = lastOrder.items.find(
        (loItem) => loItem.product === item.sku
      ) as IItemSaleInfo;
      item.quantity = Number(itemLastOrder.quantity);
      item.measureUnit = itemLastOrder.unityMeasure as EMeasureUnit;
    });

    this.initItemsSaleInfoFromLastOrder(items, lastOrder.items);

    return items;
  }

  private initItemsSaleInfoFromLastOrder(
    items: ILongSteelOrderItem[],
    lastOrderItems: ILastOrderItem[]
  ) {
    items.forEach((item) => {
      const lastOrderItem = lastOrderItems.find(
        (loi) => loi.product === item.sku
      ) as ILastOrderItem;
      const unityMeasure = item.destinations[0].measureUnits.find(
        (um) => um.measure === lastOrderItem.requestedMeasureUnit
      );

      item.itemSaleInfo = {
        material: item.sku,
        quantity: lastOrderItem.requestedQuantity.toFixed(3).toString() || '',
        unityMeasure: unityMeasure ? unityMeasure.measure : undefined
      };
    });
  }
}
